import { useRef } from 'react';
import { Loader2, Star, Trash2, Upload, ArrowLeftRight, AlertCircle } from 'lucide-react';

interface ImageItem { url: string; is_primary: boolean }

interface Props {
  images: ImageItem[];
  uploading: boolean;
  onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: (idx: number) => void;
  onSetPrimary: (idx: number) => void;
  onReorder: (from: number, to: number) => void;
}

export function Step3Photos({ images, uploading, onUpload, onRemove, onSetPrimary, onReorder }: Props) {
  const dragSrc = useRef<number | null>(null);

  return (
    <>
      <label className="flex flex-col items-center justify-center gap-2 cursor-pointer border-2 border-dashed rounded-xl p-10 hover:border-primary transition-colors text-center">
        {uploading ? <Loader2 className="h-6 w-6 animate-spin text-primary" /> : <Upload className="h-7 w-7 text-muted-foreground" />}
        <div>
          <div className="text-sm font-semibold">{uploading ? 'Uploading...' : 'Click to upload photos'}</div>
          <p className="text-xs text-muted-foreground mt-1">JPG, PNG, WEBP — up to 10MB each. Drag to reorder.</p>
        </div>
        <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} disabled={uploading} />
      </label>

      {images.length === 0 && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>Listings without photos are rarely viewed. We strongly recommend adding at least 3 high-quality images.</span>
        </div>
      )}

      {images.length > 0 && (
        <>
          <div className="text-xs text-muted-foreground flex items-center gap-1.5">
            <ArrowLeftRight className="h-3.5 w-3.5" /> Drag images to reorder. Click the star to set as cover.
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {images.map((img, i) => (
              <div
                key={i}
                draggable
                onDragStart={() => { dragSrc.current = i; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragSrc.current !== null && dragSrc.current !== i) onReorder(dragSrc.current, i);
                  dragSrc.current = null;
                }}
                className="group relative rounded-lg overflow-hidden border bg-muted cursor-move"
              >
                <img src={img.url} alt="" className="h-28 w-full object-cover" />
                {img.is_primary && (
                  <span className="absolute top-1.5 left-1.5 bg-primary text-primary-foreground text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1">
                    <Star className="h-2.5 w-2.5 fill-current" /> Cover
                  </span>
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-between">
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); onSetPrimary(i); }}
                    className="text-white text-[10px] flex items-center gap-1 hover:underline"
                  >
                    <Star className="h-3 w-3" /> Set cover
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); onRemove(i); }}
                    className="bg-destructive text-destructive-foreground rounded-full p-1"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}
